/**
 * Wallet / Top-Up / Auto-Top-Up / Cancel / Invoices / Payment-Methods.
 *
 * Currency-display rule: user-facing inputs + values are ALWAYS in Euro.
 * Internally everything stays in cents so the integer arithmetic is
 * exact, but the UI never asks the user "Cent eingeben" again.
 */
import * as React from 'react';
import {
  AlertTriangle,
  ArrowRight,
  Check,
  CheckCircle2,
  Coins,
  CreditCard,
  Download,
  FileText,
  Loader2,
  Plus,
  RefreshCw,
  Star,
  Trash2,
  Wallet,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { useToast } from '../ui/toast';
import {
  formatEUR,
  paymentMethodExpiryStatus,
  useCancelSubscription,
  useMyAdvancedAccess,
  useMyBilling,
  useMyPaymentMethods,
  useRemovePaymentMethod,
  useSetAutoTopUp,
  useSetPrimaryPaymentMethod,
  useStartAdvancedCheckout,
  useStartCheckout,
  useStartUltimateCheckout,
  useSwitchPlan,
  useTriggerManualTopUp,
  type BillingBundle,
} from '../../hooks/useBilling';
import {
  useMembershipPlans,
  useMyVisiblePlanIds,
  type MembershipPlanRow,
} from '../../hooks/useMembershipPlans';
import { useUser } from '../../auth/AuthProvider';
import { cn } from '../../lib/utils';
import {
  useMyInvoiceDownload,
  useMyInvoices,
} from '../../hooks/useInvoices';

type BillingBundleData = NonNullable<ReturnType<typeof useMyBilling>['data']>;

/** Tiny EUR <-> cents converter for input fields.
 *  Display always uses comma as decimal separator (de-DE / de-AT
 *  convention); parsing accepts both `,` and `.` so the user can type
 *  whichever they want. */
function eurToCents(eur: string): number {
  const normalized = eur.replace(',', '.').trim();
  if (!normalized) return 0;
  const v = parseFloat(normalized);
  if (!Number.isFinite(v) || v < 0) return 0;
  return Math.round(v * 100);
}
function centsToEur(cents: number | null | undefined): string {
  // Guard against null/undefined/NaN; fall back to "0,00" so inputs
  // stay editable.
  if (typeof cents !== 'number' || !Number.isFinite(cents)) return '0,00';
  return (cents / 100).toFixed(2).replace('.', ',');
}

/** Sensible defaults shown in the Auto-Top-Up form when the user lands on
 *  the page for the first time (no value persisted yet). Cents. */
const AUTO_TOPUP_RECOMMENDED_THRESHOLD_CENTS = 500;   // €5
const AUTO_TOPUP_RECOMMENDED_AMOUNT_CENTS = 1500;     // €15

// ─────────────────────────────────────────────────────────────────────
// Wallet block — Balance + Top-Up + Auto-Top-Up + Payment-Methods
// All four cards live next to each other on /usage.
// ─────────────────────────────────────────────────────────────────────

export function BalanceCard({ data }: { data: BillingBundleData }) {
  const top = useTriggerManualTopUp();
  const { toast } = useToast();
  // Default + Mindestbetrag jetzt aus den BRUTTO-Preisen — der User
  // sieht und bezahlt ausschließlich Brutto, das Backend splittet erst
  // beim Rechnungsdruck in Net + VAT.
  const defaultTopUpGross = data.pricesGross.paygDefaultTopUpCents;
  const minTopUpGross = data.pricesGross.paygMinTopUpCents;
  const [amountEur, setAmountEur] = React.useState<string>(centsToEur(defaultTopUpGross));
  const [pendingMessage, setPendingMessage] = React.useState<string | null>(null);

  async function onTopUp() {
    const cents = eurToCents(amountEur);
    if (cents < minTopUpGross) {
      toast({
        title: 'Mindest-Aufladung',
        description: `Mindestens ${formatEUR(minTopUpGross)} aufladen.`,
        variant: 'destructive',
      });
      return;
    }
    try {
      const r = await top.mutateAsync(cents);
      if (r.status === 'paid') {
        toast({
          title: 'Aufgeladen',
          description: `${formatEUR(cents)} wurden sofort gutgeschrieben.`,
        });
      } else {
        toast({
          title: 'Aufladung wird verarbeitet',
          description: `${formatEUR(cents)} — wir aktualisieren dein Guthaben automatisch sobald Mollie die Zahlung bestätigt.`,
        });
      }
      setPendingMessage(
        'Guthaben + Rechnung erscheinen hier in wenigen Sekunden, sobald die Zahlung bestätigt wurde.',
      );
      // Auto-clear the pending hint after the polling window the hook
      // schedules anyway (~15s) — by then the data has refreshed.
      window.setTimeout(() => setPendingMessage(null), 16_000);
    } catch (err) {
      toast({
        title: 'Aufladung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <Wallet className="h-5 w-5 text-primary" />
          <CardTitle>Guthaben</CardTitle>
        </div>
        <CardDescription>
          {data.billing.mode === 'subscription'
            ? `${data.billing.subscriptionMonthlyUsedMinutes.toLocaleString(
                'de-DE',
              )} / ${data.billing.subscriptionMonthlyMinutes.toLocaleString(
                'de-DE',
              )} Minuten verbraucht — Reset zum nächsten Abrechnungstermin.`
            : 'Pay-as-you-go: Wir ziehen pro Minute vom Guthaben ab.'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="text-3xl font-semibold tabular-nums">
          {formatEUR(data.billing.creditCents)}
        </div>
        {data.billing.mollieMandateId && (
          <div className="mt-4 flex items-end gap-2">
            <div className="flex-1 space-y-1.5">
              <Label htmlFor="topup">Manuell aufladen (Euro, brutto)</Label>
              <div className="relative">
                <Input
                  id="topup"
                  type="text"
                  inputMode="decimal"
                  value={amountEur}
                  onChange={(e) => setAmountEur(e.target.value)}
                  className="pr-8"
                />
                <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
                  €
                </span>
              </div>
            </div>
            <Button onClick={() => void onTopUp()} disabled={top.isPending}>
              {top.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Coins className="mr-2 h-4 w-4" />
              )}
              Aufladen
            </Button>
          </div>
        )}
        {pendingMessage && (
          <div className="mt-3 flex items-center gap-2 rounded-md border border-border bg-muted/30 p-2.5 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            <span>{pendingMessage}</span>
          </div>
        )}
        {!data.billing.mollieMandateId && (
          <div className="mt-4 rounded-md border border-dashed border-border p-3 text-center text-xs text-muted-foreground">
            Bitte zuerst eine Zahlungsmethode hinterlegen.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export function PaymentMethodsCard({ data }: { data: BillingBundleData }) {
  const methods = useMyPaymentMethods();
  const startCheckout = useStartCheckout();
  const setPrimary = useSetPrimaryPaymentMethod();
  const removeMethod = useRemovePaymentMethod();
  const { toast } = useToast();

  async function onAddMethod() {
    // Adding a new payment method = a fresh first-payment-with-mandate.
    // We use a small €1 top-up so Mollie returns a valid checkout session;
    // the €1 lands in the wallet either way, so the user gets value.
    try {
      const r = await startCheckout.mutateAsync({
        mode: 'payg',
        topUpAmountCents: 100,
      });
      window.location.href = r.checkoutUrl;
    } catch (err) {
      toast({
        title: 'Fehler beim Hinzufügen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function onMakePrimary(mandateId: string) {
    try {
      await setPrimary.mutateAsync(mandateId);
      toast({ title: 'Primäre Karte aktualisiert' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function onRemove(mandateId: string, label: string) {
    if (!confirm(`Zahlungsmethode "${label}" wirklich entfernen?`)) return;
    try {
      await removeMethod.mutateAsync(mandateId);
      toast({ title: 'Zahlungsmethode entfernt' });
    } catch (err) {
      toast({
        title: 'Konnte nicht entfernt werden',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const list = methods.data?.items ?? [];
  const visibleAlerts = list.filter((m) => {
    const s = paymentMethodExpiryStatus(m.cardExpiryDate);
    return m.status === 'valid' && (s === 'expired' || s === 'expiring_soon');
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <CreditCard className="h-5 w-5 text-primary" />
          <CardTitle>Zahlungsmethoden</CardTitle>
        </div>
        <CardDescription>
          Hinterlegt über Mollie. Auto-Top-Up + Abo-Renewal nutzen die primäre Karte.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {visibleAlerts.length > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2.5 text-xs text-amber-900 dark:border-amber-500/40 dark:bg-amber-500/10 dark:text-amber-200">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {visibleAlerts.length === 1
                ? 'Eine Zahlungsmethode läuft ab oder ist bereits abgelaufen.'
                : `${visibleAlerts.length} Zahlungsmethoden laufen ab oder sind bereits abgelaufen.`}{' '}
              Bitte rechtzeitig erneuern, damit Auto-Top-Up + Abo-Renewals weiter funktionieren.
            </span>
          </div>
        )}
        {methods.isLoading ? (
          <div className="flex h-12 items-center text-xs text-muted-foreground">
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" /> Lade Zahlungsmethoden …
          </div>
        ) : list.length === 0 ? (
          <div className="rounded-md border border-dashed border-border p-3 text-center text-sm text-muted-foreground">
            Noch keine Zahlungsmethode hinterlegt.
          </div>
        ) : (
          <div className="max-h-44 space-y-1.5 overflow-y-auto pr-1">
            {list.map((m) => (
              <PaymentMethodRow
                key={m.id}
                method={m}
                isPrimary={m.id === data.billing.mollieMandateId}
                onMakePrimary={() => void onMakePrimary(m.id)}
                onRemove={() =>
                  void onRemove(
                    m.id,
                    [m.method, m.cardLabel, m.cardNumber].filter(Boolean).join(' · ') || m.id,
                  )
                }
                busy={setPrimary.isPending || removeMethod.isPending}
              />
            ))}
          </div>
        )}
        <Button
          variant="outline"
          size="sm"
          onClick={() => void onAddMethod()}
          disabled={startCheckout.isPending}
          className="w-full"
        >
          {startCheckout.isPending ? (
            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
          ) : (
            <Plus className="mr-2 h-3.5 w-3.5" />
          )}
          Weitere Zahlungsmethode hinzufügen
        </Button>
      </CardContent>
    </Card>
  );
}

function PaymentMethodRow({
  method,
  isPrimary,
  onMakePrimary,
  onRemove,
  busy,
}: {
  method: {
    id: string;
    status: 'valid' | 'pending' | 'invalid';
    method: string | null;
    cardLabel: string | null;
    cardNumber: string | null;
    cardExpiryDate: string | null;
  };
  isPrimary: boolean;
  onMakePrimary: () => void;
  onRemove: () => void;
  busy: boolean;
}) {
  const expiryStatus = paymentMethodExpiryStatus(method.cardExpiryDate);
  const expiryLabel = formatExpiryDate(method.cardExpiryDate);

  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-muted/20 p-2 text-sm">
      <CreditCard className="h-4 w-4 text-muted-foreground" />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="font-medium">
            {method.method ?? 'Karte'}
            {method.cardLabel && ` · ${method.cardLabel}`}
          </span>
          {isPrimary && (
            <Badge variant="outline">
              <Star className="mr-1 h-2.5 w-2.5 fill-current" /> primär
            </Badge>
          )}
          <Badge
            variant={
              method.status === 'valid'
                ? expiryStatus === 'expired'
                  ? 'destructive'
                  : expiryStatus === 'expiring_soon'
                    ? 'warning'
                    : 'success'
                : method.status === 'pending'
                  ? 'warning'
                  : 'muted'
            }
          >
            {method.status === 'valid' && expiryStatus === 'expired'
              ? 'abgelaufen'
              : method.status === 'valid' && expiryStatus === 'expiring_soon'
                ? 'läuft ab'
                : method.status}
          </Badge>
        </div>
        <div className="text-xs text-muted-foreground">
          {method.cardNumber ?? '—'}
          {expiryLabel && (
            <>
              {' · '}
              <span
                className={
                  expiryStatus === 'expired'
                    ? 'text-destructive'
                    : expiryStatus === 'expiring_soon'
                      ? 'text-amber-600 dark:text-amber-300'
                      : ''
                }
              >
                gültig bis {expiryLabel}
              </span>
            </>
          )}
        </div>
      </div>
      <div className="flex shrink-0 items-center gap-1">
        {!isPrimary && method.status === 'valid' && (
          <Button
            size="sm"
            variant="ghost"
            className="h-7 text-xs"
            onClick={onMakePrimary}
            disabled={busy}
            title="Als primäre Karte verwenden"
          >
            <Star className="h-3.5 w-3.5" />
          </Button>
        )}
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-destructive hover:text-destructive"
          onClick={onRemove}
          disabled={busy}
          title="Zahlungsmethode entfernen"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}

function formatExpiryDate(raw: string | null): string | null {
  if (!raw) return null;
  // Mollie returns YYYY-MM-DD; we show MM/YYYY (typical Karten-Schema).
  const m = raw.match(/^(\d{4})-(\d{2})/);
  if (!m) return raw;
  return `${m[2]}/${m[1]}`;
}

export function AutoTopUpCard({ data }: { data: BillingBundleData }) {
  const set = useSetAutoTopUp();
  const { toast } = useToast();
  const [enabled, setEnabled] = React.useState(data.billing.autoTopUpEnabled);

  // Initial values: prefer what's persisted, fall back to the recommended
  // defaults (€5 threshold / €15 recharge). Ensures the form NEVER shows
  // "NaN" — that bug occurred when these came back as undefined and we
  // ran `(undefined / 100).toFixed(2)`.
  const initialThreshold =
    typeof data.billing.autoTopUpThresholdCents === 'number' &&
    Number.isFinite(data.billing.autoTopUpThresholdCents) &&
    data.billing.autoTopUpThresholdCents > 0
      ? data.billing.autoTopUpThresholdCents
      : AUTO_TOPUP_RECOMMENDED_THRESHOLD_CENTS;
  const initialAmount =
    typeof data.billing.autoTopUpAmountCents === 'number' &&
    Number.isFinite(data.billing.autoTopUpAmountCents) &&
    data.billing.autoTopUpAmountCents > 0
      ? data.billing.autoTopUpAmountCents
      : AUTO_TOPUP_RECOMMENDED_AMOUNT_CENTS;
  const [thresholdEur, setThresholdEur] = React.useState<string>(
    centsToEur(initialThreshold),
  );
  const [amountEur, setAmountEur] = React.useState<string>(centsToEur(initialAmount));

  async function onSave() {
    try {
      await set.mutateAsync({
        enabled,
        thresholdCents: eurToCents(thresholdEur),
        amountCents: eurToCents(amountEur),
      });
      toast({ title: 'Auto-Top-Up gespeichert' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>Auto-Top-Up</CardTitle>
          <Switch checked={enabled} onCheckedChange={setEnabled} />
        </div>
        <CardDescription>
          Wenn dein Guthaben unter den Schwellwert fällt, lädt Mollie automatisch nach.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <EuroField
            label="Schwelle"
            value={thresholdEur}
            onChange={setThresholdEur}
            disabled={!enabled}
          />
          <EuroField
            label="Aufladebetrag"
            value={amountEur}
            onChange={setAmountEur}
            disabled={!enabled}
          />
        </div>
        <div className="flex justify-end">
          <Button onClick={() => void onSave()} disabled={set.isPending}>
            {set.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-2 h-4 w-4" />
            )}
            Speichern
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * User-facing plan picker. The catalogue + ordering + visibility comes
 * from `/membershipPlans` — single source of truth. We render exactly
 * those plans the user is allowed to see (visibility filter), sorted
 * by their `sortOrder`. Hardcoded labels / prices are gone.
 *
 * Plan → action mapping is purely structural:
 *   • tier='ultimate'   + saasKind='subscription' → start Ultimate checkout
 *   • tier='enterprise' + saasKind='subscription' → start Enterprise/Advanced checkout
 *   • tier='pro'        + saasKind='subscription' → switch to anfaenger Abo (Mollie/wallet)
 *   • tier='pro'        + saasKind='payg'         → switch to anfaenger PAYG
 *
 * "isCurrent" reflects what the user has TODAY:
 *   • Enterprise/Ultimate when /advancedAccess/main is active
 *   • Pro Abo / PAYG when not active, derived from billing.mode
 *
 * The PlanSwitchDialog (Mollie redirect for first-time subscribe) is
 * still used for the anfaenger PAYG↔Abo path; Enterprise/Ultimate go
 * straight to the dedicated Mollie checkout.
 */
export function PlanSwitchCard({ data }: { data: BillingBundleData }) {
  const startAdvanced = useStartAdvancedCheckout();
  const startUltimate = useStartUltimateCheckout();
  const allPlans = useMembershipPlans();
  const visiblePlanIds = useMyVisiblePlanIds();
  const { profile } = useUser();
  const { toast } = useToast();
  const [dialogTarget, setDialogTarget] = React.useState<
    'subscription' | 'payg' | null
  >(null);

  // Bruttofaktor — alle Plan-Preise zeigen wir konsequent als Brutto an.
  // Quelle: 1) wenn auf dem Subscription-Plan beide Werte verfügbar
  // sind, nutzen wir das gemessene Verhältnis; 2) sonst den steuerlichen
  // Satz aus `data.tax.rate`; 3) Fallback 20 % (AT-Standard) damit auch
  // unkonfigurierte Konten brauchbare Werte sehen.
  const grossMultiplier = React.useMemo(() => {
    const subNet = data.plans?.subscription?.monthlyPriceNetCents ?? 0;
    const subGross = data.pricesGross?.subscriptionMonthlyCents ?? 0;
    if (subNet > 0 && subGross > 0) return subGross / subNet;
    const rate = typeof data.tax?.rate === 'number' ? data.tax.rate : 0.2;
    return 1 + rate;
  }, [data.plans, data.pricesGross, data.tax]);

  /**
   * Aktiver Plan = die tatsächliche Mitgliedschaft des Users
   * (`profile.membership.tier`) plus, für Pro, der Wallet-Modus
   * (`subscription` vs. `payg`). Wir nutzen explizit NICHT den
   * Advanced-Access-Status — der ist seit dem Wegfall des
   * Pro/Enterprise-Toggles obsolet, und ein User mit Advanced-Abo
   * aber Pro-Plan würde sonst fälschlich Enterprise als aktiv sehen.
   */
  const myTier = profile?.membership?.tier ?? null;

  function isCurrent(plan: MembershipPlanRow): boolean {
    if (plan.tier === 'ultimate') return myTier === 'ultimate';
    if (plan.tier === 'enterprise') return myTier === 'enterprise';
    if (plan.tier === 'pro' && plan.saasKind === 'subscription') {
      return myTier === 'pro' && data.billing.mode === 'subscription';
    }
    if (plan.tier === 'pro' && plan.saasKind === 'payg') {
      return myTier === 'pro' && data.billing.mode === 'payg';
    }
    return false;
  }

  async function activatePlan(plan: MembershipPlanRow) {
    try {
      if (plan.tier === 'ultimate' && plan.saasKind === 'subscription') {
        const r = await startUltimate.mutateAsync();
        if (r.checkoutUrl) window.location.href = r.checkoutUrl;
        return;
      }
      if (plan.tier === 'enterprise' && plan.saasKind === 'subscription') {
        const r = await startAdvanced.mutateAsync();
        if (r.checkoutUrl) window.location.href = r.checkoutUrl;
        return;
      }
      if (plan.tier === 'pro' && plan.saasKind === 'subscription') {
        setDialogTarget('subscription');
        return;
      }
      if (plan.tier === 'pro' && plan.saasKind === 'payg') {
        setDialogTarget('payg');
        return;
      }
      toast({
        title: 'Plan kann nicht aktiviert werden',
        description: 'Tier oder SaaS-Kind ist für den Self-Service nicht hinterlegt.',
        variant: 'destructive',
      });
    } catch (err) {
      toast({
        title: 'Aktivierung fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  // Catalogue → visible, active, sorted. Until both the plan list AND
  // the visibility filter resolve, we render a single skeleton row to
  // avoid flashing tiles the admin might have hidden.
  const ready = allPlans.isSuccess && visiblePlanIds.isSuccess;
  const visibleSet = new Set(visiblePlanIds.data ?? []);
  const tiles: MembershipPlanRow[] = !ready
    ? []
    : (allPlans.data ?? [])
        .filter((p) => p.isActive !== false)
        .filter((p) => visibleSet.has(p.id) || isCurrent(p))
        .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));

  const isPending =
    startAdvanced.isPending || startUltimate.isPending;

  return (
    <>
      <Card className="lg:col-span-2">
        <CardHeader>
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            <CardTitle>Plan wechseln</CardTitle>
          </div>
          <CardDescription>
            Restguthaben{' '}
            <strong>{formatEUR(data.billing.creditCents)}</strong> bleibt bei jedem
            Wechsel erhalten.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {!ready ? (
            <div className="flex h-24 items-center justify-center text-xs text-muted-foreground">
              <Loader2 className="mr-2 size-3.5 animate-spin" />
              Lade verfügbare Pläne …
            </div>
          ) : tiles.length === 0 ? (
            <div className="rounded-md border border-dashed border-border p-6 text-center text-xs text-muted-foreground">
              Aktuell ist kein Plan für dich freigeschaltet. Wende dich an
              deinen Administrator.
            </div>
          ) : (
            <div
              className={cn(
                'grid gap-3',
                tiles.length >= 3
                  ? 'sm:grid-cols-3'
                  : tiles.length === 2
                    ? 'sm:grid-cols-2'
                    : 'sm:grid-cols-1',
              )}
            >
              {tiles.map((plan) => {
                const cur = isCurrent(plan);
                return (
                  <PlanTile
                    key={plan.id}
                    title={plan.name}
                    priceLabel={planPriceLabel(plan, grossMultiplier)}
                    description={plan.description}
                    features={plan.features?.slice(0, 3)}
                    isCurrent={cur}
                    ctaLabel={cur ? 'Aktiver Plan' : `Auf ${plan.name} wechseln`}
                    ctaDisabled={cur}
                    loading={isPending}
                    onClick={() => void activatePlan(plan)}
                    accent={plan.tier === 'enterprise' || plan.tier === 'ultimate'}
                  />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
      {dialogTarget && (
        <PlanSwitchDialog
          target={dialogTarget}
          data={data}
          onClose={() => setDialogTarget(null)}
        />
      )}
    </>
  );
}

/**
 * Preis-Label pro Tile. Wir verrechnen ausschließlich Brutto, also
 * werden alle im MembershipPlan-Doc gespeicherten Netto-Werte mit dem
 * vom Backend gelieferten `grossMultiplier` (1 + VAT-Satz) hochgerechnet.
 *   • PAYG plans  → "X,XX € / Minute"      (brutto)
 *   • Sub plans   → "X € / Monat · Y.YYY Min"  (brutto, mit Inklusiv-Min)
 *   • Sub ohne Minutenangabe → "X € / Monat"
 *   • Free plans  → "Kostenlos"
 */
function planPriceLabel(plan: MembershipPlanRow, grossMultiplier: number): string {
  if (plan.saasKind === 'payg') {
    const netCents = plan.perMinuteNetCents ?? 0;
    const grossCents = Math.round(netCents * grossMultiplier);
    const eur = grossCents / 100;
    return `${eur.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} € / Minute`;
  }
  if (plan.priceMonthlyCents <= 0) return 'Kostenlos';
  // `priceMonthlyCents` ist im Plan-Doc als Netto hinterlegt. Damit das
  // Tile zum Dialog passt, multiplizieren wir hier identisch zum Backend.
  const grossCents = Math.round(plan.priceMonthlyCents * grossMultiplier);
  const eur = grossCents / 100;
  const priceStr = `${eur.toLocaleString('de-DE', {
    minimumFractionDigits: eur % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 2,
  })} € / Monat`;
  if (plan.callMinutesIncluded && plan.callMinutesIncluded > 0) {
    return `${priceStr} · ${plan.callMinutesIncluded.toLocaleString('de-DE')} Min`;
  }
  return priceStr;
}

/**
 * Single tier-tile inside the 3-way picker. Highlights the active
 * plan with a primary border + checkmark and disables its CTA. The
 * other two stay clickable as upgrade/downgrade targets.
 */
function PlanTile({
  title,
  priceLabel,
  description,
  features,
  isCurrent,
  ctaLabel,
  ctaDisabled,
  loading,
  onClick,
  accent,
}: {
  title: string;
  priceLabel: string;
  description: string;
  /** Optional feature bullets shown below the description. Up to 3
   *  are rendered as compact list items; the rest is hidden so the
   *  tile keeps its compact height. */
  features?: string[];
  isCurrent: boolean;
  ctaLabel: string;
  ctaDisabled?: boolean;
  loading?: boolean;
  onClick: () => void;
  accent?: boolean;
}) {
  return (
    <div
      className={
        'flex flex-col gap-3 rounded-xl border p-4 transition-colors '
        + (isCurrent
          ? 'border-primary/60 bg-primary/5'
          : accent
            ? 'border-border bg-card hover:border-primary/40'
            : 'border-border bg-card')
      }
    >
      <div className="flex items-center justify-between gap-2">
        <div>
          <div className="flex items-center gap-1.5">
            <span className="text-base font-semibold">{title}</span>
            {isCurrent && (
              <span className="inline-flex h-5 items-center gap-1 rounded-full bg-primary px-1.5 text-[10px] uppercase tracking-wider text-primary-foreground">
                <Check className="size-3" /> aktiv
              </span>
            )}
          </div>
          <div className="mt-0.5 text-xs text-muted-foreground">{priceLabel}</div>
        </div>
      </div>
      {description && (
        <p className="text-xs text-muted-foreground">{description}</p>
      )}
      {features && features.length > 0 && (
        <ul className="space-y-1 text-xs text-foreground/80">
          {features.map((f, i) => (
            <li key={i} className="flex items-start gap-1.5">
              <Check className="mt-0.5 size-3 shrink-0 text-primary" />
              <span>{f}</span>
            </li>
          ))}
        </ul>
      )}
      <Button
        size="sm"
        variant={isCurrent ? 'outline' : 'default'}
        className="mt-auto"
        onClick={onClick}
        disabled={ctaDisabled || loading}
      >
        {loading ? <Loader2 className="mr-2 size-3.5 animate-spin" /> : null}
        {ctaLabel}
      </Button>
    </div>
  );
}

/**
 * Step-by-step Dialog statt nativem `confirm()`. Erklärt was beim Wechsel
 * passiert, listet die Konsequenzen auf, und führt mit "Weiter" zu Mollie.
 */
function PlanSwitchDialog({
  target,
  data,
  onClose,
}: {
  target: 'subscription' | 'payg';
  data: BillingBundleData;
  onClose: () => void;
}) {
  const switchPlan = useSwitchPlan();
  const { toast } = useToast();

  async function onConfirm() {
    try {
      const r = await switchPlan.mutateAsync(target);
      if (r.checkoutUrl) {
        // Sofortiger Redirect — kein Toast nötig, der Mollie-Flow
        // übernimmt die Bestätigung.
        window.location.href = r.checkoutUrl;
        return;
      }
      onClose();
      toast({
        title: r.alreadyOnTarget
          ? 'Bereits auf diesem Plan'
          : r.paidFromWallet
            ? 'Erstmonat aus Guthaben bezahlt'
            : 'Plan-Wechsel angefragt',
        description: r.message,
      });
    } catch (err) {
      toast({
        title: 'Fehler beim Plan-Wechsel',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  const isToSubscription = target === 'subscription';

  // Wenn der Kunde zum Abo wechselt und das Guthaben den ersten Monat
  // deckt, wird KEIN Mollie-Redirect ausgelöst — der Erstmonat geht
  // direkt aus dem Wallet. Das Dialog-Wording muss diesen Unterschied
  // darstellen, sonst verspricht es Mollie-Redirect, der nicht kommt.
  const subscriptionGrossCents = data.pricesGross.subscriptionMonthlyCents;
  const includedMinutes = data.plans?.subscription?.monthlyIncludedMinutes ?? 0;
  const includedMinutesLabel = `${includedMinutes.toLocaleString('de-DE')} Min/Monat`;
  const walletCoversFirstMonth =
    isToSubscription &&
    data.billing.creditCents >= subscriptionGrossCents &&
    !!data.billing.mollieMandateId;

  return (
    <Dialog
      open
      onOpenChange={(o) => {
        if (!o && !switchPlan.isPending) onClose();
      }}
    >
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-primary" />
            {isToSubscription ? 'Wechsel zum Monats-Abo' : 'Wechsel zu Pay-as-you-go'}
          </DialogTitle>
          <DialogDescription>
            {isToSubscription
              ? 'Du wechselst von Pay-as-you-go auf das Monats-Abo. Folgendes passiert, sobald du bestätigst:'
              : 'Du wechselst vom Monats-Abo zurück auf Pay-as-you-go. Folgendes geschieht jetzt:'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div className="rounded-md border border-border bg-muted/30 p-3">
            <div className="flex items-center justify-between gap-2 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Aktuell
                </div>
                <div className="mt-0.5 font-semibold">
                  {isToSubscription ? 'Pay-as-you-go' : `Abo (${includedMinutesLabel})`}
                </div>
              </div>
              <ArrowRight className="h-4 w-4 text-muted-foreground" />
              <div className="text-right">
                <div className="text-xs uppercase tracking-wider text-muted-foreground">
                  Neu
                </div>
                <div className="mt-0.5 font-semibold">
                  {isToSubscription ? `Abo (${includedMinutesLabel})` : 'Pay-as-you-go'}
                </div>
              </div>
            </div>
          </div>

          <ul className="space-y-2 text-sm">
            {isToSubscription ? (
              walletCoversFirstMonth ? (
                <>
                  <Step>
                    Dein <strong>Guthaben deckt den ersten Monat ab</strong> — wir
                    ziehen{' '}
                    <strong>{formatEUR(subscriptionGrossCents)}</strong> direkt
                    aus deinem Wallet ab statt über Mollie zu belasten.
                  </Step>
                  <Step>
                    Restguthaben nach Abbuchung:{' '}
                    <strong>
                      {formatEUR(data.billing.creditCents - subscriptionGrossCents)}
                    </strong>{' '}
                    — bleibt für Überlauf-Minuten oder zukünftige Top-Ups.
                  </Step>
                  <Step>
                    Du erhältst <strong>{includedMinutesLabel}</strong> inklusive.
                  </Step>
                  <Step>
                    Folgende Monate werden automatisch über Mollie eingezogen
                    (in 30 Tagen). Monatlich kündbar.
                  </Step>
                </>
              ) : (
                <>
                  <Step>
                    Wir leiten dich zu <strong>Mollie</strong> weiter, damit du die
                    Erstzahlung von{' '}
                    <strong>{formatEUR(subscriptionGrossCents)}</strong>{' '}
                    autorisieren kannst.
                  </Step>
                  <Step>
                    Sobald die Zahlung bestätigt ist, hast du{' '}
                    <strong>{includedMinutesLabel}</strong> inklusive. Überlauf
                    wird minutengenau vom Guthaben abgerechnet.
                  </Step>
                  {data.billing.creditCents > 0 && (
                    <Step>
                      Dein bestehendes Guthaben{' '}
                      <strong>({formatEUR(data.billing.creditCents)})</strong>{' '}
                      reicht für den Erstmonat noch nicht — bleibt aber erhalten
                      und wird für Überlauf-Minuten verwendet.
                    </Step>
                  )}
                  <Step>
                    Das Abo ist <strong>monatlich kündbar</strong>.
                  </Step>
                </>
              )
            ) : (
              <>
                <Step>
                  Dein laufendes Abo wird zum <strong>Ende des aktuellen
                  Abrechnungszeitraums</strong> beendet — bis dahin gelten deine
                  Inklusiv-Minuten weiter.
                </Step>
                <Step>
                  Es wird <strong>keine weitere monatliche Abbuchung</strong> mehr
                  von Mollie ausgelöst.
                </Step>
                <Step>
                  Dein bestehendes Guthaben{' '}
                  <strong>({formatEUR(data.billing.creditCents)})</strong> bleibt
                  erhalten und wird ab dann zur Abrechnung pro Minute verwendet.
                </Step>
                <Step>Du kannst jederzeit zurück auf Abo wechseln.</Step>
              </>
            )}
          </ul>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={onClose}
            disabled={switchPlan.isPending}
          >
            Abbrechen
          </Button>
          <Button onClick={() => void onConfirm()} disabled={switchPlan.isPending}>
            {switchPlan.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <ArrowRight className="mr-2 h-4 w-4" />
            )}
            {isToSubscription ? 'Wechsel auf Monatsabo' : 'Wechsel bestätigen'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Step({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2">
      <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
      <span>{children}</span>
    </li>
  );
}

export function CancelCard({ data }: { data: BillingBundleData }) {
  const cancel = useCancelSubscription();
  const { toast } = useToast();
  if (data.billing.mode !== 'subscription') return null;

  // Already hard-cancelled.
  if (data.billing.status === 'cancelled') {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Abo beendet</CardTitle>
          <CardDescription>
            Dein Abo wurde gekündigt. Du kannst auf der Plan-Wechsel-Karte einen
            neuen Tarif starten.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  // End-of-period cancel already scheduled.
  const pendingMs = data.billing.pendingCancelAtMs;
  if (pendingMs) {
    const pending = new Date(pendingMs);
    return (
      <Card>
        <CardHeader>
          <CardTitle>Abo läuft aus</CardTitle>
          <CardDescription>
            Dein Plan endet am{' '}
            <strong>
              {pending.toLocaleDateString('de-DE', {
                day: '2-digit',
                month: 'long',
                year: 'numeric',
              })}
            </strong>
            . Bis dahin kannst du den vollen Funktionsumfang nutzen, inkl. der
            verbleibenden inkludierten Minuten. Mollie zieht keine weitere
            Zahlung mehr ein.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  async function onCancelEndOfPeriod() {
    if (!confirm('Abo zum Ende des aktuellen Abrechnungszeitraums kündigen?')) return;
    try {
      const r = await cancel.mutateAsync('end_of_period');
      const date = r.cancelAtMs
        ? new Date(r.cancelAtMs).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: 'long',
            year: 'numeric',
          })
        : 'zum nächsten Abrechnungstermin';
      toast({
        title: 'Kündigung vorgemerkt',
        description: `Dein Plan endet ${date}. Du behältst bis dahin alle inkludierten Minuten.`,
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  async function onCancelImmediate() {
    if (!confirm('Abo SOFORT beenden? Verbleibende Minuten verfallen.')) return;
    try {
      await cancel.mutateAsync('immediate');
      toast({ title: 'Sofort gekündigt', description: 'Bestätigung folgt per Mail.' });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Abo kündigen</CardTitle>
        <CardDescription>
          Standard ist eine Kündigung zum Periodenende — du behältst bis dahin
          alle inkludierten Minuten. Sofort-Kündigung verfällt verbleibende
          Minuten ohne Erstattung.
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-wrap gap-2">
        <Button
          variant="outline"
          onClick={() => void onCancelEndOfPeriod()}
          disabled={cancel.isPending}
        >
          {cancel.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
          Zum Periodenende kündigen
        </Button>
        <Button
          variant="destructive"
          onClick={() => void onCancelImmediate()}
          disabled={cancel.isPending}
        >
          Sofort beenden
        </Button>
      </CardContent>
    </Card>
  );
}

export function InvoicesListCard() {
  const invoices = useMyInvoices();
  const download = useMyInvoiceDownload();
  const { toast } = useToast();

  async function onDownload(id: string, kind: 'pdf' | 'xml') {
    try {
      const url = await download.mutateAsync({ invoiceId: id, kind });
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast({
        title: 'Download fehlgeschlagen',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-2">
          <FileText className="h-5 w-5 text-primary" />
          <CardTitle>Deine Rechnungen</CardTitle>
        </div>
        <CardDescription>
          Alle ausgestellten Rechnungen — als PDF zum Download. X-Rechnung-XML
          findest du daneben, falls du sie brauchst.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {invoices.isLoading ? (
          <div className="flex h-20 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
          </div>
        ) : (invoices.data?.items.length ?? 0) === 0 ? (
          <div className="rounded-md border border-dashed border-border p-4 text-center text-sm text-muted-foreground">
            Noch keine Rechnungen — die nächste wird automatisch nach der nächsten
            Aufladung erstellt.
          </div>
        ) : (
          // ~2 sichtbare Zeilen, Rest scrollbar — verhindert dass die
          // Karte ins Endlose wächst, sobald viele Rechnungen vorliegen.
          <div className="max-h-44 overflow-y-auto pr-1">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nr.</TableHead>
                  <TableHead>Datum</TableHead>
                  <TableHead className="text-right">Brutto</TableHead>
                  <TableHead className="text-right"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(invoices.data?.items ?? []).map((inv) => (
                  <TableRow key={inv.id}>
                    <TableCell className="font-mono text-xs">{inv.invoiceNumber}</TableCell>
                    <TableCell className="text-xs">
                      {inv.issueDate
                        ? new Date(inv.issueDate).toLocaleDateString('de-DE')
                        : '—'}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {formatEUR(inv.totals.grossCents)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs"
                          onClick={() => void onDownload(inv.id, 'pdf')}
                          disabled={!inv.pdfStoragePath}
                        >
                          <Download className="mr-1 h-3 w-3" /> PDF
                        </Button>
                        {inv.xmlStoragePath && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 text-xs"
                            onClick={() => void onDownload(inv.id, 'xml')}
                            title="X-Rechnung-XML"
                          >
                            XML
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function EuroField({
  label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <div className="relative">
        <Input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          className="pr-8"
        />
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">
          €
        </span>
      </div>
    </div>
  );
}

export type { BillingBundle };
